import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  Check,
} from 'lucide-angular';
import { WizardDataService, YesNoMaybe } from '../../../../common/wizard/wizard-data.service';
import { FacebookPixelService } from '../../../../common/analytics/facebook-pixel.service';

@Component({
  selector: 'app-step-personalize',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule, RouterLink],
  templateUrl: './step-personalize.html',
  styleUrl: './step-personalize.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepPersonalizeV2Component implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly wizardDataService = inject(WizardDataService);
  private readonly facebookPixelService = inject(FacebookPixelService);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly selectedTimeEaters = signal<string[]>([]);

  protected readonly personalizeForm: FormGroup;

  protected readonly icons = {
    Check,
  };

  protected readonly timeOptions: readonly string[] = [
    'Lesson planning',
    'Grading',
    'Assessment prep',
  ];

  protected readonly yesNoMaybeOptions: readonly string[] = ['Yes', 'No', 'Maybe'];

  constructor() {
    this.personalizeForm = this.fb.group({
      aiHelp: ['', [Validators.required]],
      usePlatform: ['', [Validators.required]],
      usePlatformReason: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    // Load data from backend first
    const existingData = await this.wizardDataService.ensureLoaded();

    // Pre-populate form with loaded data
    this.populateFormWithExistingData(existingData);
    
    if (existingData.challenges?.length) {
      this.selectedTimeEaters.set(existingData.challenges);
    }

    // Auto-save on form changes (after form is populated)
    this.personalizeForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.wizardDataService.save({
          aiHelpDescription: value.aiHelp,
          usePlatform: this.mapToYesNoMaybe(value.usePlatform),
          usePlatformReason: value.usePlatformReason,
        });
      });
  }

  private populateFormWithExistingData(existingData: ReturnType<WizardDataService['getDataSnapshot']>): void {
    const patchData: Record<string, unknown> = {};
    
    if (existingData.aiHelpDescription) patchData['aiHelp'] = existingData.aiHelpDescription;
    if (existingData.usePlatform) patchData['usePlatform'] = this.capitalizeFirst(existingData.usePlatform);
    if (existingData.usePlatformReason) patchData['usePlatformReason'] = existingData.usePlatformReason;

    if (Object.keys(patchData).length > 0) {
      this.personalizeForm.patchValue(patchData, { emitEvent: false });
    }
  }

  private mapToYesNoMaybe(value: string): YesNoMaybe | undefined {
    if (!value) return undefined;
    const lower = value.toLowerCase();
    if (lower === 'yes' || lower === 'no' || lower === 'maybe') {
      return lower as YesNoMaybe;
    }
    return undefined;
  }

  private capitalizeFirst(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  protected isTimeEaterSelected(option: string): boolean {
    return this.selectedTimeEaters().includes(option);
  }

  protected toggleTimeEater(option: string): void {
    this.selectedTimeEaters.update((current) => {
      const updated = current.includes(option)
        ? current.filter((item) => item !== option)
        : [...current, option];
      // Auto-save challenges (timeEaters)
      this.wizardDataService.save({ challenges: updated });
      return updated;
    });
  }

  protected setUsePlatform(value: string): void {
    this.personalizeForm.patchValue({ usePlatform: value });
  }

  protected onSubmit(): void {
    if (this.personalizeForm.invalid || this.selectedTimeEaters().length === 0) {
      this.personalizeForm.markAllAsTouched();
      return;
    }

    this.isLoading$.next(true);

    this.isLoading$.next(false);
    this.facebookPixelService.trackWizardComplete();
    this.router.navigate(['/dashboard']);
  }
}

